import React, {useEffect, useRef} from 'react';
import {ScrollView, StyleSheet} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {ExplorerGame} from '@/Hooks/OpeningExplorer/useOpeningExplorer';
import {GamePly} from '@/Hooks/OpeningExplorer/useExplorerGamePgn';
import {resultChip} from './index';

interface GameMovesListProps {
  meta: ExplorerGame;
  plies: GamePly[];
  /** -1 means the game's start position, before any move has been played. */
  currentPlyIndex: number;
  onSelectPly: (plyIndex: number) => void;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * PGN-style move list for a master/lichess game opened from the Opening
 * Explorer's top-games list - lives in the workspace panel (in place of the
 * chapter's own Moves/Analysis/Settings tabs) while a game tab is active,
 * since the board is showing that game's position rather than the chapter's.
 * Clicking any move (or the "Start" chip) jumps the board straight there.
 */
const GameMovesList: React.FC<GameMovesListProps> = ({
  meta,
  plies,
  currentPlyIndex,
  onSelectPly,
  isLoading,
  isError,
}) => {
  const chip = resultChip(meta);
  const scrollRef = useRef<ScrollView>(null);
  // Box (Atoms/Box) is a plain function component, not forwardRef, so it
  // can't take a React ref directly - nativeID (-> a DOM id on web) plus
  // getElementById is the reliable way to reach the active move's node for
  // auto-scroll.
  const ACTIVE_MOVE_DOM_ID = 'active-game-move-chip';

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const node = document.getElementById(ACTIVE_MOVE_DOM_ID);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }, [currentPlyIndex]);

  const pairs: Array<{
    number: number;
    white?: {san: string; ply: number};
    black?: {san: string; ply: number};
  }> = [];
  for (let i = 0; i < plies.length; i += 2) {
    pairs.push({
      number: i / 2 + 1,
      white: {san: plies[i].san, ply: i},
      black: plies[i + 1] ? {san: plies[i + 1].san, ply: i + 1} : undefined,
    });
  }

  const renderMoveChip = (move: {san: string; ply: number} | undefined) => {
    if (!move) {
      return <Box style={styles.moveChipPlaceholder} />;
    }
    const active = move.ply === currentPlyIndex;
    return (
      <Box
        nativeID={active ? ACTIVE_MOVE_DOM_ID : undefined}
        testID={`game-move-${move.ply}`}
        onPress={() => onSelectPly(move.ply)}
        style={[styles.moveChip, active && styles.moveChipActive]}>
        <CCText
          fontSize={12}
          fontWeight={active ? 'semibold' : 'regular'}
          color={active ? CCColors.TextColor.Dark : CCColors.Workbook.Brown}>
          {move.san}
        </CCText>
      </Box>
    );
  };

  return (
    <Box style={styles.container}>
      <Box style={styles.header}>
        <Box
          style={[
            styles.resultChip,
            {backgroundColor: chip.bg, borderColor: chip.border},
          ]}>
          <CCText fontSize={10} fontWeight="bold" color={chip.fg}>
            {chip.label}
          </CCText>
        </Box>
        <Box style={styles.headerNames}>
          <CCText
            fontSize={12.5}
            fontWeight="semibold"
            color={CCColors.TextColor.Dark}
            numberOfLines={1}>
            {meta.white.name}
            {meta.white.rating ? ` (${meta.white.rating})` : ''}
          </CCText>
          <CCText
            fontSize={12.5}
            fontWeight="semibold"
            color={CCColors.TextColor.Dark}
            numberOfLines={1}>
            {meta.black.name}
            {meta.black.rating ? ` (${meta.black.rating})` : ''}
          </CCText>
        </Box>
        {meta.year && (
          <CCText fontSize={11} color={CCColors.Workbook.BrownGrey}>
            {meta.year}
          </CCText>
        )}
      </Box>

      {isLoading ? (
        <Box style={styles.stateBox}>
          <CCText fontSize={12} color={CCColors.Workbook.BrownGrey}>
            Loading moves…
          </CCText>
        </Box>
      ) : isError ? (
        <Box style={styles.stateBox}>
          <Ionicons
            name="alert-circle-outline"
            size={18}
            color={CCColors.Workbook.DangerRed}
          />
          <CCText
            fontSize={12}
            color={CCColors.Workbook.DangerRed}
            style={{marginTop: 4}}>
            Couldn't load this game's moves.
          </CCText>
        </Box>
      ) : (
        <ScrollView
          ref={scrollRef}
          style={{flex: 1}}
          contentContainerStyle={styles.movesContent}
          showsVerticalScrollIndicator={false}>
          <Box
            onPress={() => onSelectPly(-1)}
            style={[
              styles.startRow,
              currentPlyIndex === -1 && styles.startRowActive,
            ]}>
            <CCText
              fontSize={11.5}
              fontWeight={currentPlyIndex === -1 ? 'semibold' : 'medium'}
              color={
                currentPlyIndex === -1
                  ? CCColors.TextColor.Dark
                  : CCColors.Workbook.BrownGrey
              }>
              Starting position
            </CCText>
          </Box>
          {pairs.map(pair => (
            <Box key={pair.number} style={styles.movePairRow}>
              <CCText
                fontSize={11.5}
                color={CCColors.Workbook.BrownGrey}
                style={styles.moveNumber}>
                {pair.number}.
              </CCText>
              {renderMoveChip(pair.white)}
              {renderMoveChip(pair.black)}
            </Box>
          ))}
        </ScrollView>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.08),
  },
  resultChip: {
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 5,
    borderWidth: 1,
  },
  headerNames: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  stateBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  movesContent: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  startRow: {
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRadius: 7,
    marginBottom: 4,
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
  } as any,
  startRowActive: {
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.16),
  },
  movePairRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 2,
  },
  moveNumber: {
    width: 26,
    textAlign: 'right',
    marginRight: 6,
  },
  moveChip: {
    flex: 1,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 6,
    marginRight: 4,
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
  } as any,
  moveChipActive: {
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.22),
  },
  moveChipPlaceholder: {
    flex: 1,
    marginRight: 4,
  },
});

export default GameMovesList;
